import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  subscriptionsApi,
  type SubscriptionPlan,
  type SubscriptionPlanParameter,
  type SubscriptionPlanParameterDefinition,
} from '@/api/subscriptions'
import { useEnumsStore } from '@/stores/enumsStore'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Collapse,
  Breadcrumbs,
  TablePagination,
  Chip,
  Skeleton,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { FilterList as FilterListIcon, MoreVert as MoreVertIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { IconButton, Menu, MenuItem as MenuItemMui } from '@mui/material'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'

type PlanFormState = {
  id?: number
  name: string
  slug: string
  description: string
  price: string
  sale_price: string
  type: string
  period: string
  interval: string
  available: boolean
  visible_in_catalog: boolean
  is_renewable: boolean
  auto_renewal: boolean
  parameters: SubscriptionPlanParameter[]
}

const emptyPlanForm = (): PlanFormState => ({
  name: '',
  slug: '',
  description: '',
  price: '',
  sale_price: '',
  type: '',
  period: '1',
  interval: 'month',
  available: true,
  visible_in_catalog: true,
  is_renewable: true,
  auto_renewal: false,
  parameters: [],
})

export function AdminSubscriptionPlansPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const enums = useEnumsStore((state) => state.enums)
  const subscriptionTypeOptions = enums?.SubscriptionType ?? []
  const [planParameterDefinitions, setPlanParameterDefinitions] = useState<SubscriptionPlanParameterDefinition[]>([])
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState<PlanFormState>(emptyPlanForm)
  const [saving, setSaving] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'unavailable'>('all')
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all')
  const [renewableFilter, setRenewableFilter] = useState<'all' | 'renewable' | 'notRenewable'>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuPlan, setMenuPlan] = useState<SubscriptionPlan | null>(null)
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 })
  const [sort, setSort] = useState<'id_desc' | 'id_asc' | 'price_asc' | 'price_desc'>('id_desc')

  const loadPlans = async () => {
    setLoading(true)
    setError(null)
    try {
      const sortField = sort.startsWith('price') ? 'price' : 'id'
      const sortOrder: 'asc' | 'desc' =
        sort.endsWith('_asc') ? 'asc' : 'desc'

      const res = await subscriptionsApi.getAdminPlans({
        search: search.trim() || undefined,
        type: typeFilter === 'all' ? undefined : typeFilter,
        available:
          availabilityFilter === 'all'
            ? undefined
            : availabilityFilter === 'available',
        visible_in_catalog:
          visibilityFilter === 'all'
            ? undefined
            : visibilityFilter === 'visible',
        is_renewable:
          renewableFilter === 'all'
            ? undefined
            : renewableFilter === 'renewable',
        sort: sortField,
        order: sortOrder,
        page: page + 1,
        per_page: rowsPerPage,
      })
      setPlans(res.data)
      setMeta(res.meta)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, typeFilter, availabilityFilter, visibilityFilter, renewableFilter, sort])

  useEffect(() => {
    subscriptionsApi
      .getPlanParameters()
      .then(setPlanParameterDefinitions)
      .catch(() => setPlanParameterDefinitions([]))
  }, [])

  const openCreateDialog = () => {
    setForm(emptyPlanForm())
    setIsEditing(false)
    setDialogOpen(true)
    setError(null)
    setSuccess(null)
  }

  const openEditDialog = (plan: SubscriptionPlan) => {
    setForm({
      id: plan.id,
      name: plan.name,
      slug: plan.slug,
      description: plan.description ?? '',
      price: plan.price,
      sale_price: plan.sale_price ?? '',
      type: plan.type,
      period: String(plan.period),
      interval: plan.interval,
      available: plan.available,
      visible_in_catalog: plan.visible_in_catalog ?? true,
      is_renewable: plan.is_renewable ?? true,
      auto_renewal: plan.auto_renewal,
      parameters: (plan.parameters ?? []).map((p) => ({
        parameter: p.parameter,
        value: p.value,
      })),
    })
    setIsEditing(true)
    setDialogOpen(true)
    setError(null)
    setSuccess(null)
  }

  const handleDialogClose = () => {
    if (saving) return
    setDialogOpen(false)
  }

  const handleFormChange = (field: keyof PlanFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined,
        price: form.price || '0',
        sale_price: form.sale_price || null,
        type: form.type,
        period: parseInt(form.period, 10),
        interval: form.interval,
        available: form.available,
        visible_in_catalog: form.visible_in_catalog,
        is_renewable: form.is_renewable,
        auto_renewal: form.auto_renewal,
        parameters: planParameterDefinitions.map((def) => ({
          parameter: def.parameter,
          value: String(
            form.parameters.find((p) => p.parameter === def.parameter)?.value ?? def.default
          ).trim(),
        })),
      }

      if (!form.name || !form.slug || !form.price || !form.period) {
        setError(t('admin.plans.validationRequired') as string)
        setSaving(false)
        return
      }

      if (isEditing && form.id) {
        await subscriptionsApi.updatePlan(form.id, payload)
        setSuccess(t('admin.plans.updated') as string)
      } else {
        await subscriptionsApi.createPlan(payload)
        setSuccess(t('admin.plans.created') as string)
      }

      setDialogOpen(false)
      await loadPlans()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, unknown[]> } }; message?: string }
      const msg =
        error?.response?.data?.message ||
        (error?.response?.data?.errors && Object.values(error.response.data.errors).flat().join('. ')) ||
        error?.message ||
        t('common.error')
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (plan: SubscriptionPlan) => {
    if (!window.confirm(t('admin.plans.deleteConfirm', { name: plan.name }) as string)) return
    setError(null)
    setSuccess(null)
    try {
      await subscriptionsApi.deletePlan(plan.id)
      setSuccess(t('admin.plans.deleted') as string)
      await loadPlans()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      const msg = error?.response?.data?.message || error?.message || t('common.error')
      setError(msg)
    } finally {
      // no-op
    }
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setPage(0)
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, plan: SubscriptionPlan) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuPlan(plan)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuPlan(null)
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.plans.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.plans.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-plans" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Toolbar: search + filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('admin.plans.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>{t('admin.plans.sort')}</InputLabel>
            <Select
              label={t('admin.plans.sort')}
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
            >
              <MenuItem value="id_desc">{t('admin.plans.sortIdDesc')}</MenuItem>
              <MenuItem value="id_asc">{t('admin.plans.sortIdAsc')}</MenuItem>
              <MenuItem value="price_asc">{t('admin.plans.sortPriceAsc')}</MenuItem>
              <MenuItem value="price_desc">{t('admin.plans.sortPriceDesc')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant={filtersOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
            sx={{ height: 40 }}
          >
            {t('admin.plans.filtersButton')}
          </Button>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={openCreateDialog}
              sx={{ height: 40 }}
            >
              {t('admin.plans.create')}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>{t('admin.plans.filterType')}</InputLabel>
              <Select
                label={t('admin.plans.filterType')}
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="all">{t('admin.plans.all')}</MenuItem>
                {subscriptionTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>{t('admin.plans.filterAvailability')}</InputLabel>
              <Select
                label={t('admin.plans.filterAvailability')}
                value={availabilityFilter}
                onChange={(e) =>
                  setAvailabilityFilter(e.target.value as typeof availabilityFilter)
                }
              >
                <MenuItem value="all">{t('admin.plans.all')}</MenuItem>
                <MenuItem value="available">{t('admin.plans.available')}</MenuItem>
                <MenuItem value="unavailable">{t('admin.plans.unavailable')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>{t('admin.plans.filterVisibility')}</InputLabel>
              <Select
                label={t('admin.plans.filterVisibility')}
                value={visibilityFilter}
                onChange={(e) =>
                  setVisibilityFilter(e.target.value as typeof visibilityFilter)
                }
              >
                <MenuItem value="all">{t('admin.plans.all')}</MenuItem>
                <MenuItem value="visible">{t('admin.plans.visible')}</MenuItem>
                <MenuItem value="hidden">{t('admin.plans.hidden')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>{t('admin.plans.filterRenewable')}</InputLabel>
              <Select
                label={t('admin.plans.filterRenewable')}
                value={renewableFilter}
                onChange={(e) =>
                  setRenewableFilter(e.target.value as typeof renewableFilter)
                }
              >
                <MenuItem value="all">{t('admin.plans.all')}</MenuItem>
                <MenuItem value="renewable">{t('admin.plans.renewable')}</MenuItem>
                <MenuItem value="notRenewable">{t('admin.plans.notRenewable')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Paper key={i} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Skeleton variant="text" width={40} height={20} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
                  <Skeleton variant="text" width={80} height={20} sx={{ mt: 1 }} />
                  <Skeleton variant="text" width={60} height={20} sx={{ mt: 0.5 }} />
                  <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1, mt: 1 }} />
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('admin.plans.name')}</TableCell>
                    <TableCell>{t('admin.plans.price')}</TableCell>
                    <TableCell>{t('admin.plans.duration')}</TableCell>
                    <TableCell>{t('admin.plans.type')}</TableCell>
                    <TableCell>{t('admin.plans.settings')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={120} />
                        <Skeleton variant="text" width={80} height={18} />
                      </TableCell>
                      <TableCell><Skeleton variant="text" width={70} /></TableCell>
                      <TableCell><Skeleton variant="text" width={40} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : meta.total === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.plans.empty')}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {plans.map((plan) => {
              const typeLabel =
                subscriptionTypeOptions.find((opt) => opt.value === plan.type)?.label ?? plan.type
              return (
                <Paper key={plan.id} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      #{plan.id}
                    </Typography>
                    <IconButton size="small" onClick={(e) => openMenu(e, plan)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography fontWeight={600}>{plan.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plan.slug}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('admin.plans.price')}: {parseFloat(plan.price).toLocaleString('ru-RU', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    ₽
                    {plan.sale_price && (
                      <Typography variant="body2" color="text.secondary" component="span">
                        {' '}
                        / {parseFloat(plan.sale_price).toLocaleString('ru-RU', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        ₽
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.plans.duration')}: {plan.duration}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={typeLabel} />
                  </Box>
                </Paper>
              )
            })}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('admin.plans.name')}</TableCell>
                  <TableCell>{t('admin.plans.price')}</TableCell>
                  <TableCell>{t('admin.plans.duration')}</TableCell>
                  <TableCell>{t('admin.plans.type')}</TableCell>
                  <TableCell>{t('admin.plans.settings')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>{plan.id}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{plan.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plan.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {parseFloat(plan.price).toLocaleString('ru-RU', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      ₽
                      {plan.sale_price && (
                        <Typography variant="body2" color="text.secondary" component="span">
                          {' '}
                          / {parseFloat(plan.sale_price).toLocaleString('ru-RU', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          ₽
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{plan.duration}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          subscriptionTypeOptions.find((opt) => opt.value === plan.type)?.label ??
                          plan.type
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="ul"
                        sx={{ m: 0, pl: 2, '& li': { mb: 0.5, fontSize: '0.875rem' } }}
                      >
                        <li>
                          {plan.available
                            ? t('admin.plans.available')
                            : t('admin.plans.unavailable')}
                        </li>
                        <li>
                          {(plan.visible_in_catalog ?? true)
                            ? t('admin.plans.visible')
                            : t('admin.plans.hidden')}
                        </li>
                        <li>
                          {(plan.is_renewable ?? true)
                            ? t('admin.plans.renewable')
                            : t('admin.plans.notRenewable')}
                        </li>
                        <li>
                          {plan.auto_renewal
                            ? t('admin.plans.autoRenewalOn')
                            : t('admin.plans.autoRenewalOff')}
                        </li>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, plan)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItemMui
            onClick={() => {
              if (menuPlan) openEditDialog(menuPlan)
              closeMenu()
            }}
          >
            {t('admin.plans.edit')}
          </MenuItemMui>
          <MenuItemMui
            onClick={() => {
              if (menuPlan) handleDelete(menuPlan)
              closeMenu()
            }}
          >
            {t('admin.plans.delete')}
          </MenuItemMui>
        </Menu>

        {!loading && meta.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
            <TablePagination
              component="div"
              count={meta.total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage={t('admin.plans.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('admin.plans.displayedRows', {
                  from,
                  to,
                  count,
                })
              }
            />
          </Box>
        )}

        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="md">
          <DialogTitle>
            {isEditing ? t('admin.plans.editTitle', { name: form.name }) : t('admin.plans.createTitle')}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('admin.plans.name')}
                required
                fullWidth
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('admin.plans.slug')}
                  required
                  fullWidth
                  value={form.slug}
                  onChange={(e) => handleFormChange('slug', e.target.value)}
                />
                <FormControl fullWidth required>
                  <InputLabel>{t('admin.plans.type')}</InputLabel>
                  <Select
                    label={t('admin.plans.type')}
                    value={form.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                  >
                    {subscriptionTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label={t('admin.plans.description')}
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('admin.plans.price')}
                  type="number"
                  required
                  fullWidth
                  value={form.price}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                />
                <TextField
                  label={t('admin.plans.sale_price')}
                  type="number"
                  fullWidth
                  value={form.sale_price}
                  onChange={(e) => handleFormChange('sale_price', e.target.value)}
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('admin.plans.period')}
                  type="number"
                  required
                  fullWidth
                  value={form.period}
                  onChange={(e) => handleFormChange('period', e.target.value)}
                />
                <FormControl fullWidth>
                  <InputLabel required>{t('admin.plans.interval')}</InputLabel>
                  <Select
                    label={t('admin.plans.interval')}
                    value={form.interval}
                    onChange={(e) => handleFormChange('interval', e.target.value)}
                  >
                    <MenuItem value="day">{t('admin.plans.intervalDay')}</MenuItem>
                    <MenuItem value="week">{t('admin.plans.intervalWeek')}</MenuItem>
                    <MenuItem value="month">{t('admin.plans.intervalMonth')}</MenuItem>
                    <MenuItem value="year">{t('admin.plans.intervalYear')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t('subscription.parameters')}
                </Typography>
                {planParameterDefinitions.map((def) => {
                  const existing = form.parameters.find((p) => p.parameter === def.parameter)
                  const value = existing?.value ?? def.default
                  const isBoolean = def.type === 'boolean'
                  return (
                    <Box key={def.parameter} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 220, flex: 1 }}
                      >
                        {def.label}
                      </Typography>
                      {isBoolean ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value === 'true'}
                              onChange={(e) => {
                                const newValue = e.target.checked ? 'true' : 'false'
                                setForm((prev) => {
                                  const others = prev.parameters.filter((p) => p.parameter !== def.parameter)
                                  return {
                                    ...prev,
                                    parameters: [...others, { parameter: def.parameter, value: newValue }],
                                  }
                                })
                              }}
                            />
                          }
                          label=""
                        />
                      ) : (
                        <TextField
                          label={t('admin.plans.parameterValue')}
                          type={def.type === 'integer' ? 'number' : 'text'}
                          sx={{ minWidth: 200, flex: 1 }}
                          value={value}
                          onChange={(e) => {
                            const newValue = e.target.value
                            setForm((prev) => {
                              const others = prev.parameters.filter((p) => p.parameter !== def.parameter)
                              const trimmed = String(newValue).trim()
                              return {
                                ...prev,
                                parameters:
                                  trimmed === '' || trimmed === def.default
                                    ? others
                                    : [...others, { parameter: def.parameter, value: trimmed }],
                              }
                            })
                          }}
                        />
                      )}
                    </Box>
                  )
                })}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.available}
                      onChange={(e) => handleFormChange('available', e.target.checked)}
                    />
                  }
                  label={t('admin.plans.available')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.visible_in_catalog}
                      onChange={(e) => handleFormChange('visible_in_catalog', e.target.checked)}
                    />
                  }
                  label={t('admin.plans.visible')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_renewable}
                      onChange={(e) => handleFormChange('is_renewable', e.target.checked)}
                    />
                  }
                  label={t('admin.plans.renewable')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.auto_renewal}
                      onChange={(e) => handleFormChange('auto_renewal', e.target.checked)}
                    />
                  }
                  label={t('admin.plans.autoRenewal')}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
            <Button onClick={handleDialogClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}

